import { useState, useEffect } from 'react';
import { 
  Search, GraduationCap, CheckCircle, XCircle, Clock,
  Loader2, AlertCircle, User, Award
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rangos = [
  'Profesional Inmobiliario',
  'Profesional Inmobiliario Avanzado',
  'Licenciado Inmobiliario',
  'Máster Inmobiliario',
  'Máster Inmobiliario Avanzado',
  'Doctor Inmobiliario',
  'Doctor Honoris Causa',
];

function getStatusConfig(status) {
  if (status === 'Verificado') return { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/20', Icon: CheckCircle };
  if (status === 'Completado') return { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', Icon: AlertCircle };
  if (status === 'Verificación Pendiente') return { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', Icon: Clock };
  return { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20', Icon: Clock };
}

export function AdminEnrollments() {
  const { token } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [verifyForm, setVerifyForm] = useState({ verified: false, observaciones: '', update_rank: false, new_rank: '' });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchEnrollments();
  }, []);

  async function fetchEnrollments() {
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/admin/enrollments/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setEnrollments(await response.json());
      }
    } catch (error) {
      console.error('Error fetching enrollments:', error);
    } finally {
      setIsLoading(false);
    }
  }

  function handleVerify(enrollment) {
    setSelectedEnrollment(enrollment);
    setVerifyForm({ verified: false, observaciones: '', update_rank: false, new_rank: enrollment.professional?.rango || '' });
    setIsModalOpen(true);
  }

  async function handleSaveVerification() {
    if (!selectedEnrollment) return;
    setIsSaving(true);

    try {
      const response = await fetch(`${API}/admin/enrollment/${selectedEnrollment.id}/verify`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(verifyForm)
      });

      if (response.ok) {
        setIsModalOpen(false);
        fetchEnrollments();
      } else {
        const error = await response.json();
        alert(error.detail || 'Error al verificar');
      }
    } catch (error) {
      console.error('Error verifying:', error);
    } finally {
      setIsSaving(false);
    }
  }

  const filteredEnrollments = enrollments.filter(function(e) {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    if (e.program_nombre && e.program_nombre.toLowerCase().includes(searchLower)) return true;
    if (e.professional && e.professional.nombre && e.professional.nombre.toLowerCase().includes(searchLower)) return true;
    return false;
  });

  function renderEnrollmentCard(enrollment, index) {
    const config = getStatusConfig(enrollment.status);
    const StatusIcon = config.Icon;

    return (
      <div key={enrollment.id || index} className="bg-[#111111] border border-[#2a2a2a] rounded-xl p-6 hover:border-[#3a3a3a] transition-colors">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-lg ${config.bg} flex items-center justify-center`}>
              <GraduationCap className={`w-6 h-6 ${config.text}`} />
            </div>
            <div>
              <h3 className="text-white font-medium">{enrollment.program_nombre}</h3>
              <p className="text-sm text-gray-500">{enrollment.program_horas} horas | {enrollment.program_tipo}</p>
            </div>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
            <StatusIcon className="w-3 h-3 inline mr-1" />{enrollment.status}
          </span>
        </div>

        {enrollment.professional && (
          <div className="flex items-center gap-2 mb-4 p-3 bg-[#0a0a0a] rounded-lg">
            <User className="w-4 h-4 text-gray-500" />
            <div>
              <span className="text-gray-300">{enrollment.professional.nombre}</span>
              <span className="text-gray-500 text-sm ml-2">• {enrollment.professional.rango || 'Sin rango'}</span>
            </div>
          </div>
        )}

        {enrollment.fecha_inscripcion && (
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
            <Clock className="w-4 h-4" />
            Inscrito: {new Date(enrollment.fecha_inscripcion).toLocaleDateString('es-MX')}
          </div>
        )}

        {(enrollment.status === 'Completado' || enrollment.status === 'Verificación Pendiente') && (
          <Button onClick={() => handleVerify(enrollment)} className="w-full bg-white text-black hover:bg-gray-200">
            <CheckCircle className="w-4 h-4 mr-2" />
            Verificar Finalización
          </Button>
        )}
      </div>
    );
  }

  const cards = [];
  for (let i = 0; i < filteredEnrollments.length; i++) {
    cards.push(renderEnrollmentCard(filteredEnrollments[i], i));
  }

  return (
    <div className="p-8" data-testid="admin-enrollments">
      <div className="mb-8">
        <h1 className="font-manrope font-bold text-2xl text-white mb-2">Verificación de Cursos</h1>
        <p className="text-gray-400">Verifica la finalización de cursos y actualiza el rango de los profesionales</p>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input placeholder="Buscar por curso o profesional..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-[#111111] border-[#2a2a2a] text-white" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-white" />
        </div>
      ) : cards.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{cards}</div>
      ) : (
        <div className="bg-[#111111] border border-[#2a2a2a] rounded-xl p-12 text-center">
          <GraduationCap className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No hay cursos pendientes de verificación</p>
        </div>
      )}

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-[#111111] border-[#2a2a2a] text-white">
          <DialogHeader><DialogTitle className="text-white">Verificar Finalización de Curso</DialogTitle></DialogHeader>
          {selectedEnrollment && (
            <div className="space-y-6">
              <div className="bg-[#0a0a0a] rounded-lg p-4">
                <p className="text-sm text-gray-400 mb-1">Curso</p>
                <p className="text-white font-medium">{selectedEnrollment.program_nombre}</p>
                <p className="text-sm text-gray-500">{selectedEnrollment.program_horas} horas</p>
              </div>

              {selectedEnrollment.professional && (
                <div className="bg-[#0a0a0a] rounded-lg p-4">
                  <p className="text-sm text-gray-400 mb-1">Profesional</p>
                  <p className="text-white">{selectedEnrollment.professional.nombre}</p>
                  <p className="text-sm text-gray-500">Rango actual: {selectedEnrollment.professional.rango || 'Sin rango'}</p>
                </div>
              )}

              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Button
                    type="button"
                    variant={verifyForm.verified ? "default" : "outline"}
                    onClick={() => setVerifyForm({ ...verifyForm, verified: true })}
                    className={verifyForm.verified ? "bg-green-500 hover:bg-green-400 text-black" : "border-[#2a2a2a] text-gray-400"}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />Aprobar
                  </Button>
                  <Button
                    type="button"
                    variant={!verifyForm.verified ? "default" : "outline"}
                    onClick={() => setVerifyForm({ ...verifyForm, verified: false })}
                    className={!verifyForm.verified ? "bg-red-500 hover:bg-red-400 text-white" : "border-[#2a2a2a] text-gray-400"}
                  >
                    <XCircle className="w-4 h-4 mr-2" />Rechazar
                  </Button>
                </div>

                {verifyForm.verified && (
                  <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <input
                        type="checkbox"
                        id="update_rank"
                        checked={verifyForm.update_rank}
                        onChange={(e) => setVerifyForm({ ...verifyForm, update_rank: e.target.checked })}
                        className="rounded"
                      />
                      <Label htmlFor="update_rank" className="text-gray-300">Actualizar rango del profesional</Label>
                    </div>
                    
                    {verifyForm.update_rank && (
                      <Select value={verifyForm.new_rank} onValueChange={(v) => setVerifyForm({ ...verifyForm, new_rank: v })}>
                        <SelectTrigger className="bg-[#0a0a0a] border-[#2a2a2a] text-white">
                          <SelectValue placeholder="Seleccionar nuevo rango" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a]">
                          <SelectItem value="Profesional Inmobiliario">Profesional Inmobiliario</SelectItem>
                          <SelectItem value="Profesional Inmobiliario Avanzado">Profesional Inmobiliario Avanzado</SelectItem>
                          <SelectItem value="Licenciado Inmobiliario">Licenciado Inmobiliario</SelectItem>
                          <SelectItem value="Máster Inmobiliario">Máster Inmobiliario</SelectItem>
                          <SelectItem value="Máster Inmobiliario Avanzado">Máster Inmobiliario Avanzado</SelectItem>
                          <SelectItem value="Doctor Inmobiliario">Doctor Inmobiliario</SelectItem>
                          <SelectItem value="Doctor Honoris Causa">Doctor Honoris Causa</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}

                <div>
                  <Label className="text-gray-300">Observaciones</Label>
                  <Textarea
                    value={verifyForm.observaciones}
                    onChange={(e) => setVerifyForm({ ...verifyForm, observaciones: e.target.value })}
                    className="mt-1 bg-[#0a0a0a] border-[#2a2a2a] text-white"
                    placeholder="Notas sobre la verificación..."
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setIsModalOpen(false)} className="border-[#2a2a2a] text-gray-400">
                  Cancelar
                </Button>
                <Button onClick={handleSaveVerification} disabled={isSaving} className="bg-white text-black hover:bg-gray-200">
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Guardar Verificación
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
